import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

const confidenceColors = {
  high: 'success',
  medium: 'warning',
  low: 'error',
};

const statusLabels = {
  pending: 'Pending',
  resolved_rp: 'Kept Ours',
  resolved_platform: 'Used Platform',
  dismissed: 'Dismissed',
};

const fieldLabels = {
  price: 'Price',
  name: 'Name',
  quantity: 'Quantity',
  sku: 'SKU',
};

const formatFieldValue = (field, value) => {
  if (value == null || value === '') return '(empty)';
  if (field === 'price') return `$${parseFloat(value).toFixed(2)}`;
  return String(value);
};

const ConflictRow = ({ conflict, onResolve, isResolving }) => {
  const [expanded, setExpanded] = useState(false);
  const isPending = conflict.status === 'pending';

  return (
    <>
      <TableRow sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
        <TableCell sx={{ width: 40, p: 1 }}>
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            aria-label={expanded ? 'Collapse details' : 'Expand details'}
          >
            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {conflict.rpData?.itemTitle || conflict.platformData?.name || 'Unknown Item'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {conflict.rpData?.itemNumber && `RP: ${conflict.rpData.itemNumber}`}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            label={conflict.matchConfidence}
            size="small"
            color={confidenceColors[conflict.matchConfidence] || 'default'}
            variant="outlined"
          />
        </TableCell>
        <TableCell>
          <Typography variant="caption">
            {conflict.matchCriteria}
          </Typography>
        </TableCell>
        <TableCell>
          {isPending ? (
            <Chip label="Pending" size="small" color="warning" variant="filled" />
          ) : (
            <Chip
              label={statusLabels[conflict.status] || conflict.status}
              size="small"
              color="default"
              variant="outlined"
              icon={<CheckCircleIcon />}
            />
          )}
        </TableCell>
        <TableCell align="right">
          {isPending && (
            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
              <Tooltip title="Keep Reseller Pro values">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onResolve(conflict.id, 'rp')}
                  disabled={isResolving}
                  sx={{ textTransform: 'none', minWidth: 'auto', px: 1.5 }}
                >
                  Keep Ours
                </Button>
              </Tooltip>
              <Tooltip title="Apply platform values to Reseller Pro">
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => onResolve(conflict.id, 'platform')}
                  disabled={isResolving}
                  sx={{ textTransform: 'none', minWidth: 'auto', px: 1.5 }}
                >
                  Use Platform
                </Button>
              </Tooltip>
              <Tooltip title="Dismiss this conflict">
                <Button
                  size="small"
                  variant="text"
                  color="inherit"
                  onClick={() => onResolve(conflict.id, 'dismiss')}
                  disabled={isResolving}
                  sx={{ textTransform: 'none', minWidth: 'auto', px: 1 }}
                >
                  Dismiss
                </Button>
              </Tooltip>
            </Box>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }} colSpan={6}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Field Differences
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Field</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Reseller Pro</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Platform</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(conflict.fields || []).map((field) => (
                    <TableRow key={field.field}>
                      <TableCell>
                        <Typography variant="body2">
                          {fieldLabels[field.field] || field.field}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatFieldValue(field.field, field.rpValue)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatFieldValue(field.field, field.platformValue)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const ConflictManager = ({
  open,
  onClose,
  conflicts = [],
  isLoading,
  isResolving,
  onResolve,
  onResolveAll,
  onFilterChange,
  channelName,
}) => {
  const [statusFilter, setStatusFilter] = useState('pending');
  const [bulkResolution, setBulkResolution] = useState('platform');

  const handleFilterChange = (e) => {
    const newStatus = e.target.value;
    setStatusFilter(newStatus);
    onFilterChange?.(newStatus);
  };

  const pendingCount = conflicts.filter(c => c.status === 'pending').length;
  const filteredConflicts = statusFilter === 'all'
    ? conflicts
    : conflicts.filter(c => c.status === statusFilter);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
          Inventory Conflicts{channelName ? ` — ${channelName}` : ''}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {pendingCount > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {pendingCount} conflict{pendingCount !== 1 ? 's' : ''} need{pendingCount === 1 ? 's' : ''} review.
            Items matched between Reseller Pro and the platform have different values for one or more fields.
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={handleFilterChange}
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="resolved_rp">Kept Ours</MenuItem>
              <MenuItem value="resolved_platform">Used Platform</MenuItem>
              <MenuItem value="dismissed">Dismissed</MenuItem>
            </Select>
          </FormControl>

          {pendingCount > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Resolution</InputLabel>
                <Select
                  value={bulkResolution}
                  label="Resolution"
                  onChange={(e) => setBulkResolution(e.target.value)}
                >
                  <MenuItem value="platform">Use Platform</MenuItem>
                  <MenuItem value="rp">Keep Ours</MenuItem>
                  <MenuItem value="dismiss">Dismiss</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                size="small"
                onClick={() => onResolveAll?.(bulkResolution)}
                disabled={isResolving}
                startIcon={isResolving ? <CircularProgress size={14} color="inherit" /> : null}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
              >
                Resolve All ({pendingCount})
              </Button>
            </Box>
          )}
        </Box>

        {isLoading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredConflicts.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              {statusFilter === 'pending' ? 'No pending conflicts.' : 'No conflicts found.'}
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }} />
                  <TableCell sx={{ fontWeight: 700 }}>Item</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Confidence</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Matched By</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredConflicts.map((conflict) => (
                  <ConflictRow
                    key={conflict.id}
                    conflict={conflict}
                    onResolve={onResolve}
                    isResolving={isResolving}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose} sx={{ textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConflictManager;
